/* eslint-disable react/require-default-props */
import React from 'react';
import {
  Field, FieldProps, FastField, FastFieldProps,
} from 'formik';
import {
  KatInput, KatTextarea, KatDropdown, KatRadiobuttonGroup, KatCheckbox,
} from '@amzn/katal-react';
import { useTranslation } from 'react-i18next';
import ImageUpload from './ImageUpload';

export type FormikFieldProps = {
  name: string;
  component: React.ComponentType<any> | React.ElementType;
  fast?: boolean,
  [otherProps: string]: any;
};

const FormikField = ({
  name, component: Component, fast, ...rest
}: FormikFieldProps) => {
  const { t } = useTranslation();
  const FieldComponent = fast ? FastField : Field;
  return (
    <FieldComponent name={name}>
      {({ field, form }: FieldProps | FastFieldProps) => {
        const { value, onChange, onBlur } = field;
        const {
          touched, errors, setFieldValue,
        } = form;
        let errorMessage;
        if (touched[name] && errors[name]) {
          const errorList = (errors[name] as string).split(',');
          if (errorList.length > 1) {
            errorMessage = errorList.map((error) => t(error)).join('. ');
          } else {
            errorMessage = t(errorList[0]);
          }
        }

        switch (Component) {
          case KatInput:
          case KatTextarea:
            return (
              <Component
                name={name}
                value={value}
                onInput={onChange}
                onChange={onChange}
                onBlur={onBlur}
                state={errorMessage ? 'error' : undefined}
                stateLabel={errorMessage}
                {...rest}
              />
            );
          case KatDropdown:
            return (
              <Component
                name={name}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                state={errorMessage ? 'error' : undefined}
                stateLabel={errorMessage}
                {...rest}
              />
            );
          case KatRadiobuttonGroup:
            return (
              <Component
                name={name}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                errorLabel={errorMessage}
                {...rest}
              />
            );
          case KatCheckbox:
            return (
              <Component
                name={name}
                onChange={() => setFieldValue(name, !value, true)}
                onBlur={onBlur}
                checked={value}
                {...rest}
              />
            );
          case ImageUpload:
            return (
              <>
                <Component
                  name={name}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  {...rest}
                />
                <div className="text-kat-crimson">{errorMessage}</div>
              </>
            );
          default:
            return (
              <Component
                name={name}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                error={errorMessage}
                {...rest}
              />
            );
        }
      }}
    </FieldComponent>
  );
};

export default FormikField;
